import { User } from "./User";
import { Application } from "../applications/Application";
import { APPLICATION } from "../../application";
import { RoleName, StandardRole } from "./Role";
import { filterAccountServiceRoles } from "./ApplicationRoles";

export function isAdmin(user: User) {
  return hasAccountServiceRole(user, StandardRole.ADMIN)
}

export function isApplicationAdmin(user: User) {
  return hasAccountServiceRole(user, StandardRole.APPLICATION_ADMIN)
}

export function isCompanyAdmin(user: User) {
  return hasAccountServiceRole(user, StandardRole.COMPANY_ADMIN)
}

/**
 * A user may have more than one account service role: the derived APPLICATION_ADMIN role can be combined
 * with the assigned COMPANY_ADMIN role (SSO-292).
 */
export function hasAccountServiceRole(user: User, role: RoleName) {
  return user.applicationRoles.some(applicationRole => applicationRole.application.name === APPLICATION && applicationRole.role === role)
}

export function isAdminForApplication(user: User, application: Application) {
  const applicationRoles = user.applicationRoles
    .filter(applicationRole => applicationRole.application.name === application.name) // Roles in current application
    .filter(applicationRole => applicationRole?.role === application.adminRole) // Check if one role is admin role
  return applicationRoles.length > 0
}

// The most powerful role comes first
const ACCOUNT_SERVICE_ROLE_PRIORITY: RoleName[] = [ StandardRole.ADMIN, StandardRole.COMPANY_ADMIN, StandardRole.APPLICATION_ADMIN, StandardRole.USER ]

/**
 * Returns the most powerful account service role of the user, e.g. for display purposes.
 * Use [hasAccountServiceRole] for permission checks, since a user may have more than one account service role.
 */
export function getAccountServiceRole(user: User) {
  const accountServiceRoles = user.applicationRoles.filter(applicationRole => applicationRole.application.name === APPLICATION)
  const priority = (role: RoleName) => {
    const index = ACCOUNT_SERVICE_ROLE_PRIORITY.indexOf(role)
    return index < 0 ? ACCOUNT_SERVICE_ROLE_PRIORITY.length : index
  }
  return [ ...accountServiceRoles ].sort((a, b) => priority(a.role) - priority(b.role))[0]!!
}

/**
 * Returns true if the account is an application admin for the given user, i.e. the user has a role in at least one
 * application the account administers. Mirrors SecurityChecks.mayAccessUser in the backend.
 * @param account The current application admin user
 * @param user The user which is being changed
 * @param allApplications
 */
export function isApplicationAdminForUser(account: User, user: User, allApplications: Application[]): boolean {
  if (!isApplicationAdmin(account)) {
    return false
  }
  return filterAccountServiceRoles(user.applicationRoles).some(applicationRole => {
    const application = allApplications.find(application => application.name === applicationRole.application.name)
    return !!application && isAdminForApplication(account, application)
  })
}
